import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import BodyContentType from 'owa-service/lib/contract/BodyContentType';
import BusyType from 'owa-service/lib/contract/BusyType';
import CalendarItemExperienceType from 'owa-service/lib/contract/CalendarItemExperienceType';
import CalendarItemTypeType from 'owa-service/lib/contract/CalendarItemTypeType';
import EffectiveRightsType from 'owa-service/lib/contract//EffectiveRightsType';
import EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import InboxReminderType from 'owa-service/lib/contract/InboxReminderType';
import ItemId from 'owa-service/lib/contract/ItemId';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import SensitivityType from 'owa-service/lib/contract/SensitivityType';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import { ClientFolderId, ClientItemId } from 'owa-client-ids';
import { DisplayDate } from 'owa-datetime';
import { TxpObject } from 'txp-data';

export type CalendarItemTypeTypeEx = CalendarItemTypeType | 'ThisAndFollowingInstancesMaster';

/**
 * Calendar Item definition: Add properties here from the CalendarItem and Item contracts as needed.
 * Mobx has a problem with optionals, so this serves as our wrapper around the calendar item contract type
 * and will be what we use in forms such that the contract type is only used in the service layer.
 */
interface CalendarEvent {
    // Properties from Item contract
    ItemId: ClientItemId;
    ParentFolderId: ClientFolderId;
    Subject: string;
    ReminderIsSet: boolean;
    ReminderMinutesBeforeStart: number;
    LastModifiedTime: DisplayDate;
    InstanceKey: string;
    Preview: string;
    EffectiveRights: EffectiveRightsType;
    HasAttachments: boolean;
    Attachments: AttachmentType[];
    Sensitivity: SensitivityType;
    Body: BodyContentType;
    EntityNamesMap: number;
    IsDraft: boolean;
    Categories: string[];

    // Properties from CalendarItem contract
    __type: string;
    Start: DisplayDate;
    End: DisplayDate;
    IsAllDayEvent: boolean;
    FreeBusyType: BusyType;
    IsMeeting: boolean;
    IsCancelled: boolean;
    IsRecurring: boolean;
    IsResponseRequested: boolean;
    CalendarItemType: CalendarItemTypeTypeEx;
    ResponseType: ResponseTypeType;
    Organizer: SingleRecipientType;
    RequiredAttendees: AttendeeType[];
    OptionalAttendees: AttendeeType[];
    Resources: AttendeeType[];
    AppointmentReplyTime: DisplayDate;
    Recurrence: RecurrenceType;
    Location: EnhancedLocation;
    IntendedFreeBusyStatus: BusyType;
    IsOrganizer: boolean;
    AppointmentReplyName: string;
    IsSeriesCancelled: boolean;
    CharmId: number;
    Locations: EnhancedLocation[];
    ClientSeriesId: string;
    UID: string;
    ExperienceType: CalendarItemExperienceType;
    DoNotForwardMeeting: boolean;
    IsOnlineMeeting: boolean;
    JoinOnlineMeetingUrl: string;
    InboxReminders: InboxReminderType[];
    MeetingRequestWasSent: boolean;
    ExtractionSourceId: ItemId;
    StartTimeZoneId: string;
    EndTimeZoneId: string;
    OnlineMeetingProvider: string;
    OnlineMeetingJoinUrl: string;
    OnlineMeetingConferenceId: string;
    OnlineMeetingTollNumber: string;
    OnlineMeetingTollFreeNumbers: string[];
    SeriesMasterItemId: ItemId;
    ExtractionSourceInternetMessageId: string;
    HideAttendees: boolean;

    // Calculated Properties
    TxpObject: TxpObject;

    // Birthday Event Properties
    PersonId: ItemId;
    InReplyTo: string;

    // Free/Busy View Event Properties
    IsFreeBusyOnly?: boolean;
}

/* Creates empty instance of CalendarEvent with undefined properties
 * so that mobX doesn't freak out and can observe on those properties when they get assigned
 */
export function createEmptyCalendarEvent() {
    let emptyCalendarEvent: CalendarEvent = {
        __type: undefined,
        ItemId: undefined,
        ParentFolderId: undefined,
        Subject: undefined,
        ReminderIsSet: undefined,
        ReminderMinutesBeforeStart: undefined,
        LastModifiedTime: undefined,
        InstanceKey: undefined,
        Preview: undefined,
        EffectiveRights: undefined,
        HasAttachments: undefined,
        Attachments: undefined,
        Sensitivity: undefined,
        Body: undefined,
        EntityNamesMap: undefined,
        IsDraft: undefined,
        Categories: undefined,
        Start: undefined,
        End: undefined,
        IsAllDayEvent: undefined,
        FreeBusyType: undefined,
        IsMeeting: undefined,
        IsCancelled: undefined,
        IsRecurring: undefined,
        IsResponseRequested: undefined,
        CalendarItemType: undefined,
        ResponseType: undefined,
        Organizer: undefined,
        RequiredAttendees: undefined,
        OptionalAttendees: undefined,
        Resources: undefined,
        AppointmentReplyTime: undefined,
        Recurrence: undefined,
        Location: undefined,
        IntendedFreeBusyStatus: undefined,
        IsOrganizer: undefined,
        AppointmentReplyName: undefined,
        IsSeriesCancelled: undefined,
        CharmId: undefined,
        Locations: undefined,
        ClientSeriesId: undefined,
        UID: undefined,
        ExperienceType: undefined,
        DoNotForwardMeeting: undefined,
        IsOnlineMeeting: undefined,
        JoinOnlineMeetingUrl: undefined,
        InboxReminders: undefined,
        MeetingRequestWasSent: undefined,
        ExtractionSourceId: undefined,
        TxpObject: undefined,
        StartTimeZoneId: undefined,
        EndTimeZoneId: undefined,
        OnlineMeetingProvider: undefined,
        OnlineMeetingJoinUrl: undefined,
        OnlineMeetingConferenceId: undefined,
        OnlineMeetingTollNumber: undefined,
        OnlineMeetingTollFreeNumbers: undefined,
        SeriesMasterItemId: undefined,
        ExtractionSourceInternetMessageId: undefined,
        HideAttendees: undefined,
        PersonId: undefined,
        InReplyTo: undefined,
        IsFreeBusyOnly: undefined,
    };

    return emptyCalendarEvent;
}

export default CalendarEvent;
